/**
 * Reddit Advertising Integration Module (Story 19.10)
 *
 * Barrel export for OAuth, REST helpers, errors, and the Reddit Ads adapter.
 *
 * Reddit Ads characteristics worth knowing about before reading this code:
 *  - OAuth 2.0 + PKCE against the Reddit OAuth 2.0 endpoints
 *    (`https://www.reddit.com/api/v1/authorize` for authorize,
 *     `https://www.reddit.com/api/v1/access_token` for token)
 *  - **HTTP Basic Authentication** on the token endpoint (matches Pinterest,
 *    diverges from Snapchat / Microsoft Ads / Klaviyo which use form-body
 *    credentials). `client_id` / `client_secret` go in the Authorization
 *    header, NOT the body.
 *  - **`User-Agent` header REQUIRED** on every API call. Reddit treats it as
 *    the per-app rate-limit key — env-suffixed (production / staging) so
 *    deploys are differentiated as separate Reddit rate-quota buckets.
 *  - **`duration=permanent` MANDATORY** on /authorize — without it Reddit
 *    returns a 1-hour access token with NO refresh_token.
 *  - **Refresh tokens DO NOT rotate** — refresh response OMITS the field;
 *    adapter carries forward `credentials.refreshToken` unchanged. **This
 *    INVERTS the Snapchat 19.7 defensive-rotation pattern.**
 *  - Aggressive 60-minute access TTL — `inlineRefreshOn401: true` is REQUIRED
 *    by the TD4 invariant (access TTL < 24h)
 *  - Discovery is a 2-level Business→AdAccount hierarchy, fetched sequentially
 *    (NOT parallel — preserves rate quota fairness across concurrent users)
 *  - Provider account ID is the lex-asc-min id (Reddit IDs are short
 *    alphanumeric, e.g. `a2_abc123`)
 *  - Sales-gated API access reality: AC #13 surfaces `api_not_enabled` mapping
 *    when a customer's Reddit account lacks Ads API approval
 *  - RFC 8941 structured rate-limit headers (`RateLimit-Policy` / `RateLimit`)
 *  - **Story 19.11 wires the metric pipeline** (121 primary metrics + ~40
 *    custom-event slot variants, 21 dimensions). 19.10 ships the adapter shell
 *    with empty `getAvailableMetrics()` / `getAvailableDimensions()`.
 *
 * The folder is named `reddit/` (not `reddit-ads/`) to leave room for future
 * Reddit properties (Reddit OAuth login, organic Reddit content APIs),
 * mirroring how `pinterest/` / `microsoft/` / `snapchat/` are structured.
 */

import { registerAdapter } from "../registry";
import { redditAdsAdapter } from "./reddit-ads";

// Register the adapter so getAdapter("reddit-ads") returns it.
registerAdapter("reddit-ads", redditAdsAdapter);

// Platform values (shared constant for frontend/backend alignment)
export const REDDIT_ADS_PLATFORM_VALUES = ["reddit-ads"] as const;
export type RedditAdsPlatform = (typeof REDDIT_ADS_PLATFORM_VALUES)[number];

// OAuth utilities
export {
  buildRedditAdsAuthUrl,
  exchangeRedditAdsCode,
  refreshRedditAdsToken,
  parseRedditAdsCallbackError,
  validateTokenResponse,
  validateScope,
  extractOAuthErrorCode,
  extractOAuthErrorDescription,
  buildBasicAuthHeader,
  getRedditUserAgent,
  REDDIT_ADS_SCOPES,
  REDDIT_REVOKE_URL,
  RedditAdsTokenError,
  type RedditAdsTokenErrorOptions,
  type RedditAdsAuthUrlOptions,
  type RedditAdsTokenResponse,
  type RedditAdsCallbackError,
} from "./oauth";

// REST helpers
export {
  buildRedditHeaders,
  parseRedditRateLimitHeaders,
  fetchRedditAdsMe,
  fetchRedditAdsBusinesses,
  fetchRedditAdsAdAccountsByBusiness,
  fetchRedditAdsBusinessesAndAdAccounts,
  safeFetchReddit,
  REDDIT_ADS_API_BASE,
  REDDIT_MAX_BUSINESSES,
  REDDIT_MAX_AD_ACCOUNTS_PER_BUSINESS,
  REDDIT_BASE_BACKOFF_MS,
  REDDIT_MAX_RETRIES,
  type RedditAdsUserInfo,
  type RedditAdAccount,
  type RedditBusiness,
  type RedditBusinessesResult,
  type RedditRateLimit,
  type SafeFetchRedditOk,
} from "./api";

// Reports (Story 19.11 Task 1)
export {
  buildRedditReportRequest,
  buildRedditReportRequestForAsyncChunk,
  computeRedditAsyncChunkCount,
  REDDIT_ADS_DELIVERY_ONLY_DIMENSIONS,
  REDDIT_ADS_DEPRECATED_METRIC_FIELDS,
  REDDIT_ADS_MAX_ASYNC_CHUNK_DAYS,
  REDDIT_ADS_MAX_BREAKDOWNS_PER_REQUEST,
  REDDIT_ADS_MAX_BREAKDOWNS_WITH_COUNTRY_REGION,
  REDDIT_ADS_MAX_FETCHMETRICS_DAYS,
  REDDIT_ADS_MAX_HOUR_BREAKDOWN_DAYS,
  REDDIT_ADS_MAX_PARALLEL_CHUNKS,
  REDDIT_ADS_MAX_SUBREDDITS_PER_REPORT,
  REDDIT_ADS_REACH_FREQ_AVAILABLE_FROM,
  REDDIT_ADS_REPORTS_ENDPOINT,
  REDDIT_REPORTS_TIMEOUT_MS,
  type RedditAdsAggregationLevel,
  type RedditAdsBreakdown,
  type RedditAdsBreakdownAlias,
  type RedditAdsBreakdownInput,
  type RedditAdsClickAttributionWindow,
  type RedditAdsReportRequest,
  type RedditAdsReportRequestOptions,
  type RedditAdsReportRequestWarning,
  type RedditAdsReportType,
  type RedditAdsViewAttributionWindow,
} from "./reports";

// Analytics (Story 19.11 Tasks 2 + 3)
export {
  DEFAULT_REDDIT_ADS_FAN_OUT_PLAN,
  REDDIT_ADS_CAMPAIGNS_PAGE_SIZE,
  REDDIT_ADS_MAX_CAMPAIGNS_PAGES,
  REDDIT_ADS_TOP_ADS_LIMIT,
  REDDIT_ADS_TOP_AD_GROUPS_LIMIT,
  REDDIT_ADS_TOP_CAMPAIGNS_LIMIT,
  REDDIT_FATAL_ERROR_CODES,
  REDDIT_FATAL_ERROR_PRIORITY,
  discoverActiveCampaigns,
  fetchRedditAdAccountMetadata,
  parseConversionValue,
  parseMicroCurrency,
  parseRedditMetric,
  parseRedditPercent,
  parseRedditReportResponse,
  pickTopAdsByImpressions,
  pickTopAdGroupsBySpend,
  pickTopByMetric,
  pickTopCampaignsBySpend,
  pickTopSubredditsByImpressions,
  resolveRedditFatalPriority,
  runRedditAdsAsyncChunked,
  runRedditAdsParallelPipelines,
  runRedditAdsSyncPipeline,
  type RedditAdAccountMetadata,
  type RedditAdsFanOutPlan,
  type RedditAdsPipelineFailure,
  type RedditAdsPipelineResult,
  type RedditAdsPipelineSuccess,
  type RedditAdsPipelineWarning,
  type RedditAdsSyncPipelineOptions,
  type RunRedditAdsAsyncChunkedInput,
  type RunRedditAdsAsyncChunkedResult,
  type RunRedditAdsParallelInput,
  type RunRedditAdsParallelResult,
} from "./analytics";

// Catalogue (Story 19.11 Task 5)
export {
  REDDIT_ADS_API_FIELDS,
  REDDIT_ADS_CUSTOM_EVENT_SLOTS,
  getRedditAdsApiFieldByKey,
  getRedditAdsKeyByApiField,
  validateRedditAdsFieldList,
} from "./catalogue";

// Error parsing
export {
  parseRedditAdsApiError,
  redditAdsErrorTypeToAdapterCode,
  isAdsApiNotEnabledError,
  RedditAdsApiCallError,
  type RedditAdsApiError,
  type RedditAdsErrorType,
} from "./errors";

// Adapter
export {
  redditAdsAdapter,
  REDDIT_ADS_METRICS,
  REDDIT_ADS_DIMENSIONS,
  REDDIT_ADS_API_VERSION,
  REDDIT_ADS_DEFAULT_FETCH_FIELDS,
  buildRedditAdsCacheKey,
} from "./reddit-ads";
